import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [inputKey, setInputKey] = useState(0); // Force re-render key
  const [showPassword, setShowPassword] = useState(false);

  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();

  // Reset loading state on component mount to prevent stuck disabled inputs
  useEffect(() => {
    setIsLoading(false);
    setError('');
    setInputKey(0);
  }, []);

  // Additional safety: Check for stuck loading state periodically
  useEffect(() => {
    const checkStuckState = () => {
      if (isLoading) {
        console.log('[LOGIN] Checking for stuck loading state...');
        // If loading state persists for more than 30 seconds, force reset
        const timeout = setTimeout(() => {
          console.warn('[LOGIN] Forcing reset due to stuck loading state');
          setIsLoading(false);
          setError('État bloqué détecté - champs débloqués automatiquement');
          setInputKey(prev => prev + 1);
        }, 30000);
        return () => clearTimeout(timeout);
      }
    };

    return checkStuckState();
  }, [isLoading]);

  // Safety timeout to reset loading state if it gets stuck
  useEffect(() => {
    if (isLoading) {
      const timeout = setTimeout(() => {
        console.warn('[LOGIN] Loading state timeout - resetting');
        setIsLoading(false);
        setError('Timeout de connexion. Veuillez réessayer.');
      }, 15000); // Reduced to 15 seconds for better UX

      return () => clearTimeout(timeout);
    }
  }, [isLoading]);


  // Handle input changes with safety reset
  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isLoading) {
      console.warn('[LOGIN] Resetting stuck loading state on email change');
      setIsLoading(false);
      setError('');
    }
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isLoading) {
      console.warn('[LOGIN] Resetting stuck loading state on password change');
      setIsLoading(false);
      setError('');
    }
    setPassword(e.target.value);
  };

  // Force reset function for manual recovery
  const forceReset = () => {
    console.log('[LOGIN] Force resetting all states');
    setIsLoading(false);
    setError('');
    setEmail('');
    setPassword('');
    setInputKey(prev => prev + 1); // Force input re-render
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isLoading) return;

    setIsLoading(true);
    setError('');

    try {
      await login(email, password);

      // Attendre un peu pour que le user soit chargé
      await new Promise(resolve => setTimeout(resolve, 100));

      // Récupérer l'utilisateur du store pour vérifier son rôle
      const currentUser = useAuthStore.getState().user;
      const userType = localStorage.getItem('userType');

      console.log('[LOGIN] User après login:', currentUser);
      console.log('[LOGIN] User type:', userType);

      // Rediriger selon le rôle
      if (currentUser?.role === 'client') {
        console.log('[LOGIN] Redirection vers Client dashboard');
        navigate('/client/dashboard');
      } else if (currentUser?.role === 'client_member' || userType === 'client_member') {
        navigate('/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('[LOGIN] Error during login:', err);
      
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      
      if (detail === 'unverified' || (status === 403 && detail === 'unverified')) {
        // Rediriger vers la page de validation au lieu d'afficher une erreur
        navigate('/resend-verification', { 
          state: { unverified: true, email: email } 
        });
      } else if (detail === 'inactive' || (status === 403 && detail === 'inactive')) {
        setError("Votre compte est actuellement désactivé. Veuillez contacter l'administrateur.");
      } else if (status === 401) {
        setError("Email ou mot de passe incorrect.");
      } else {
        setError(err.message || 'Erreur de connexion');
      }
    } finally {
      // Ensure loading state is always reset
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image/Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden border-r border-gray-200 items-center justify-center">

        {/* Background Image */}
        <div
          className="absolute inset-0 bg-[#fff7ec]"
        ></div>
        {/* Dark Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/0"></div>

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center p-12 text-center">
          <img
            src="/NOVA - Goutte ALLIANCE.png"
            alt="Nova logo"
            className="w-64 h-64 object-contain mb-8"
          />
          <h1 className="text-4xl font-bold mb-3 font-josefin tracking-[0.2em] text-lime-800">NOVA</h1>
          <p className="text-lg text-gray-600 max-w-md">
            Notre solution de gestion relaxante
          </p>
        </div>

        {/* Decorative Elements */}
        <div className="absolute top-10 right-10 w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
        <div className="absolute bottom-10 left-10 w-32 h-32 bg-purple-400/20 rounded-full blur-2xl"></div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-slate-900 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Mobile Logo */}
          <div className="text-center lg:hidden">
            <img
              src="/NOVA - Goutte ALLIANCE.png"
              alt="Nova logo"
              className="w-20 h-20 object-contain mx-auto mb-4 rounded-xl"
            />
            <h1 className="text-2xl font-bold text-emerald-700 dark:text-emerald-400 font-josefin tracking-[0.4em]">NOVA</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Notre solution de gestion relaxante</p>
          </div>

          <Card className="shadow-xl border-0 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm">
            <CardHeader className="text-center">
              <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">Connexion</CardTitle>
              <CardDescription className="text-gray-600 dark:text-gray-400">
                Entrez vos identifiants pour accéder au système
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {(error || isLoading) && (
                  <div className="space-y-2">
                    {error && (
                      <div className="flex items-center gap-2 p-3 text-sm text-red-800 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                        <AlertCircle className="h-4 w-4" />
                        {error}
                      </div>
                    )}
                    {(isLoading || error) && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={forceReset}
                        className="w-full"
                      >
                        {isLoading ? 'Débloquer les champs' : 'Réinitialiser le formulaire'}
                      </Button>
                    )}
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-gray-700 dark:text-gray-300">Email</Label>
                  <Input
                    key={`email-${inputKey}`}
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={handleEmailChange}
                    placeholder="votre@email.com"
                    required
                    disabled={isLoading}
                    className={`${isLoading ? 'opacity-50' : ''} bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 transition-opacity focus-visible:ring-[#2f855a] focus-visible:border-[#2f855a] dark:focus-visible:ring-emerald-400 dark:focus-visible:border-emerald-400`}
                    autoComplete="email"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-gray-700 dark:text-gray-300">Mot de passe</Label>
                  <div className="relative">
                    <Input
                      key={`password-${inputKey}`}
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={handlePasswordChange}
                      placeholder="••••••••"
                      required
                      disabled={isLoading}
                      className={`${isLoading ? 'opacity-50' : ''} bg-white dark:bg-slate-700 border-gray-300 dark:border-slate-600 transition-opacity pr-10 focus-visible:ring-[#2f855a] focus-visible:border-[#2f855a] dark:focus-visible:ring-emerald-400 dark:focus-visible:border-emerald-400`}
                      autoComplete="current-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
                      disabled={isLoading}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </div>

                {isLoading && (
                  <div className="text-sm text-[#2f855a] dark:text-emerald-400 text-center">
                    Connexion en cours...
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-[#2f855a] hover:bg-[#276749] text-white font-semibold py-3 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Connexion...
                    </>
                  ) : (
                    'Se connecter'
                  )}
                </Button>

                <div className="text-center mt-4 space-y-2">
                  <Link
                    to="/forgot-password"
                    className="block text-sm text-[#2f855a] hover:text-[#276749] dark:text-emerald-400 dark:hover:text-emerald-300 transition-colors duration-200"
                  >
                    Mot de passe oublié ?
                  </Link>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Vous êtes un nouveau client ?{' '}
                    <Link
                      to="/client/register"
                      className="text-[#2f855a] hover:text-[#276749] dark:text-emerald-400 dark:hover:text-emerald-300 font-medium transition-colors duration-200"
                    >
                      Inscrivez-vous
                    </Link>
                  </p>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Powered by Digitalia */}
          <div className="mt-6 flex flex-col items-center space-y-2">
            <img
              src="/digit.png"
              alt="Digitalia"
              className="h-8 w-auto object-contain opacity-70 hover:opacity-100 transition-opacity duration-200"
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
              Powered by <span className="text-gray-700 dark:text-gray-300 font-semibold">Digitalia</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
